// src/components/Auth/IntroPage.tsx
import React, { useState, useEffect } from 'react';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { useInviteRequest } from '../../hooks/useInviteRequest';
import { Link } from 'react-router-dom';

interface IntroPageProps {
  onRequestSuccess: (data: { email: string; name: string; isWaitlisted: boolean; userExists?: boolean; hasValidatedCode?: boolean }) => void;
}

export function IntroPage({ onRequestSuccess }: IntroPageProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const { requestInvite, isLoading, error: requestError } = useInviteRequest();
  const [localError, setLocalError] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  const error = localError || requestError;

  // Validate email format using regex
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  
  // Validate name format - letters, spaces, hyphens only, proper length
  const validateName = (name: string): boolean => {
    // Only allow letters, spaces, and hyphens
    const re = /^[A-Za-z\s\-']+$/;
    
    // Check length (2-50 characters)
    return name.length >= 2 && name.length <= 50 && re.test(name);
  };

  // Field-specific validation functions
  const validateNameField = (value: string): string => {
    if (!value.trim()) {
      return 'Name is required';
    }
    if (value.length < 2) {
      return 'Name must be at least 2 characters';
    }
    if (value.length > 50) {
      return 'Name cannot exceed 50 characters';
    }
    if (!/^[A-Za-z\s\-']+$/.test(value)) {
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return '';
  };

  const validateEmailField = (value: string): string => {
    if (!value.trim()) {
      return 'Email is required';
    }
    if (!validateEmail(value)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  // Handle field changes with validation
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setNameError(validateNameField(value));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmailField(value));
  };
  
/*   const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setLocalError('');
    
    // Validate fields
    const nameValidationError = validateNameField(name);
    const emailValidationError = validateEmailField(email);
    
    setNameError(nameValidationError);
    setEmailError(emailValidationError);

    // Check if any validation errors exist
    if (nameValidationError || emailValidationError) {
      return;
    }

    try {
      const result = await requestInvite({ name: name.trim(), email: email.trim() });
      
      if (result.success) {
        // If user is redirected to login page or waitlisted, store that info
        if (result.userExists) {
          // Store in localStorage that user already exists
          localStorage.setItem('tripperUserExists', 'true');
          localStorage.setItem('tripperUserEmail', email.trim());
        }
        
        // Navigate to login step directly
        onRequestSuccess({ 
          email: email.trim(),
          name: name.trim(),
          isWaitlisted: false,
          userExists: true // Add this flag
        });
      } else {
        // Normal flow - invite sent or waitlisted
        onRequestSuccess({ 
          email: email.trim(),
          name: name.trim(),
          isWaitlisted: result.waitlisted || false,
          userExists: false
        });
      }
    }
    } catch (err) {
      setLocalError('Failed to request invite code');
    }
  }; */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setLocalError('');
    
    // Validate fields
    const nameValidationError = validateNameField(name);
    const emailValidationError = validateEmailField(email);
    
    setNameError(nameValidationError);
    setEmailError(emailValidationError);
  
    // Check if any validation errors exist
    if (nameValidationError || emailValidationError) {
      return;
    }
  
    try {
      const result = await requestInvite({ name: name.trim(), email: email.trim() });
      console.log("[IntroPage] Invite request result -------------------- I AM IN INTROPAGE !!!!!!!!!");
      if (result.success) {
        if ((result as any).userExists) {
          // User already exists - go to login
          localStorage.setItem('tripperUserExists', 'true');
          localStorage.setItem('tripperHasAccount', 'true');
          localStorage.setItem('tripperUserEmail', email.trim());
          localStorage.setItem('tripperFullName', name.trim());
          localStorage.setItem('tripperCodeValidated', 'true');
          
          onRequestSuccess({ 
            email: email.trim(),
            name: name.trim(),
            isWaitlisted: false,
            userExists: true,
            hasValidatedCode: true
          });
        } else if ((result as any).hasValidatedCode) {
          // Already validated code - go to signup
          localStorage.setItem('tripperCodeValidated', 'true');
          localStorage.setItem('tripperUserEmail', email.trim());
          localStorage.setItem('tripperFullName', name.trim());
          
          onRequestSuccess({ 
            email: email.trim(),
            name: name.trim(),
            isWaitlisted: false,
            userExists: false,
            hasValidatedCode: true
          });
        } else {
          // Normal flow - invite sent
          localStorage.setItem('tripperFullName', name.trim());
          onRequestSuccess({ 
            email: email.trim(),
            name: name.trim(),
            isWaitlisted: result.waitlisted || false,
            userExists: false
          });
        }
      } else {
        setLocalError(result.message || 'Failed to process invite request');
      }
    } catch (err) {
      setLocalError('Failed to request invite code');
    }
  };

  return (
    <>
      {/* Header */}
      {/* <div className="px-6 py-4 border-b border-gray-200 flex items-center">
        <span className="text-2xl mr-2">🌏</span>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Welcome to Tripper</h2>
          <p className="text-sm text-gray-500">The AI Powered Smart Travel Experience</p>
        </div>
      </div> */}
      <div className="px-6 flex items-center border-b border-gray-200" style={{ 
        height: 'var(--auth-panel-header-height)',
        padding: 'var(--auth-panel-padding)'
      }}>
        <span className="text-3xl flex-shrink-0" style={{ 
          marginRight: '.5rem',
          marginTop: '2px' // Slight adjustment for visual alignment
        }}>🌏</span>
        <div className="flex-1">
          <h2 className="text-gray-900 leading-tight" style={{ 
            fontSize: 'var(--auth-font-size-2xl)',
            fontWeight: 'var(--auth-font-weight-regular)',
            fontFamily: 'var(--auth-header-font-family)',
            marginBottom: '0.125rem'
          }}>Welcome to Tripper</h2>
          <p className="text-gray-500" style={{ 
            fontSize: 'var(--auth-font-size-sm)',
            lineHeight: 'var(--auth-line-height-sm)',
            marginTop: '0'
          }}>The AI Powered Smart Travel Experience</p>
        </div>
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Name Input */}
        <div>
          <label htmlFor="name" className="block text-gray-700" style={{
            fontSize: 'var(--auth-label-font-size)',
            fontWeight: 'var(--auth-label-font-weight)',
            marginBottom: 'var(--auth-label-margin-bottom)'
          }}>
            Full Name
          </label>
          <input
            type="text"
            id="name"
            name="name" // Set name to help browser distinguish this field
            autoComplete="name" // Use correct autocomplete attribute
            value={name}
            onChange={handleNameChange}
            className={`w-full border ${nameError ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-blue-500`}
            style={{
              height: 'var(--auth-input-height)',
              padding: 'var(--auth-input-padding)',
              fontSize: 'var(--auth-input-font-size)',
              fontFamily: 'var(--auth-font-family)'
            }}
            placeholder="Enter your full name"
            disabled={isLoading}
            maxLength={50} // Hard limit on input length
          />
          {nameError && (
            <p className="mt-1 text-red-500" style={{
              fontSize: 'var(--auth-error-font-size)',
              fontWeight: 'var(--auth-error-font-weight)'
            }}>{nameError}</p>
          )}
        </div>

        {/* Email Input */}
        <div>
          <label htmlFor="name" className="block text-gray-700" style={{
            fontSize: 'var(--auth-label-font-size)',
            fontWeight: 'var(--auth-label-font-weight)',
            marginBottom: 'var(--auth-label-margin-bottom)'
          }}>
            Email Address
          </label>
          <input
            type="email" // Use email type for better validation
            id="email"
            name="email" // Set name to help browser distinguish this field
            autoComplete="email" // Use correct autocomplete attribute
            value={email}
            onChange={handleEmailChange}
            style={{
              height: 'var(--auth-input-height)',
              padding: 'var(--auth-input-padding)',
              fontSize: 'var(--auth-input-font-size)',
              fontFamily: 'var(--auth-font-family)'
            }}
            className={`w-full px-4 py-2 border ${emailError ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            placeholder="Enter your email address"
            disabled={isLoading}
          />
          {emailError && (
            <p className="mt-1 text-red-500 text-sm">{emailError}</p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Privacy Notice */}
      {/*   <div className="text-xs text-gray-500">
        By requesting an invite, you agree to our <a href="/terms-of-use.html" className="text-blue-600 hover:underline" target="_blank">Terms of Use</a> and <a href="/privacy-policy.html" className="text-blue-600 hover:underline" target="_blank">Privacy Policy</a>.
        </div> */}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading || !!nameError || !!emailError}
          className={`w-full bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-2
            ${isLoading || !!nameError || !!emailError
              ? 'bg-blue-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 transition-colors'
            }`}
           style={{
            height: 'var(--auth-button-height)',
            padding: 'var(--auth-button-padding)',
            fontSize: 'var(--auth-button-font-size)',
            fontWeight: 'var(--auth-button-font-weight)',
            borderRadius: 'var(--auth-button-border-radius)'
          }}  
        >
          {isLoading ? (
            <span>Checking Details...</span>
          ) : (
            <>
              <span>Get Started</span>
              <ArrowRight size={18} />
            </>
          )}
        </button>
      </form>
    </>
  );
}

